import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box, Card, CardContent, TextField, Button, Typography, Alert,
  CircularProgress, InputAdornment, IconButton, MenuItem,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Eye, EyeOff, User, Lock, Building2 } from 'lucide-react';
import { useAuth } from '../auth/AuthProvider';

// Selectable login tenants. BSF Bank holds the live book (default first);
// the zero-UUID Default tenant is kept for platform/admin access.
const TENANTS = [
  { id: 'a573db8d-40b8-4ac8-96d4-b96e9d7aadd0', label: 'BSF Bank' },
  { id: '00000000-0000-0000-0000-000000000000', label: 'Default' },
];

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, loading, error, login } = useAuth();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [tenantId, setTenantId] = useState(TENANTS[0].id);
  const [showPassword, setShowPassword] = useState(false);
  const [formError, setFormError] = useState('');

  useEffect(() => {
    if (isAuthenticated) {
      const from = (location.state as any)?.from?.pathname || '/';
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, location]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    if (!username.trim()) { setFormError('Username is required'); return; }
    if (!password) { setFormError('Password is required'); return; }
    try {
      await login({ username: username.trim(), password, tenant_id: tenantId });
    } catch { /* error captured in auth state */ }
  };

  const theme = useTheme();

  return (
    <Box sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      bgcolor: 'background.default',
      backgroundImage: `radial-gradient(ellipse at 50% 0%, ${theme.palette.background.paper} 0%, ${theme.palette.background.default} 70%)`,
    }}>
      <Card sx={{
        maxWidth: 420,
        width: '100%',
        mx: 2,
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
      }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Box sx={{
              width: 36,
              height: 36,
              borderRadius: 0.5,
              bgcolor: 'action.hover',
              border: '1px solid',
              borderColor: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <Typography sx={{ color: 'primary.main', fontWeight: 700, fontSize: '0.8rem', fontFamily: '"JetBrains Mono", monospace' }}>QF</Typography>
            </Box>
            <Typography variant="h5" fontWeight={600} sx={{ color: 'text.primary' }}>QFinPricer</Typography>
          </Box>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
            Sign in to access quantitative pricing analytics
          </Typography>

          {(error || formError) && <Alert severity="error" sx={{ mb: 2 }}>{formError || error}</Alert>}

          <form onSubmit={handleSubmit}>
            <TextField
              select fullWidth label="Tenant" value={tenantId}
              onChange={(e) => setTenantId(e.target.value)}
              disabled={loading} sx={{ mb: 2 }}
              slotProps={{ input: { startAdornment: <InputAdornment position="start"><Building2 size={18} color={theme.palette.text.secondary} /></InputAdornment> } }}
            >
              {TENANTS.map((t) => (
                <MenuItem key={t.id} value={t.id}>{t.label}</MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth label="Username" value={username}
              onChange={(e) => setUsername(e.target.value)}
              disabled={loading} autoComplete="username" autoFocus sx={{ mb: 2 }}
              slotProps={{ input: { startAdornment: <InputAdornment position="start"><User size={18} color={theme.palette.text.secondary} /></InputAdornment> } }}
            />
            <TextField
              fullWidth label="Password" type={showPassword ? 'text' : 'password'}
              value={password} onChange={(e) => setPassword(e.target.value)}
              disabled={loading} autoComplete="current-password" sx={{ mb: 3 }}
              slotProps={{
                input: {
                  startAdornment: <InputAdornment position="start"><Lock size={18} color={theme.palette.text.secondary} /></InputAdornment>,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" size="small" sx={{ color: 'text.secondary' }}>
                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />
            <Button
              type="submit" variant="contained" fullWidth size="large" disabled={loading}
              sx={{
                py: 1.2,
                bgcolor: 'action.hover',
                color: 'primary.main',
                border: '1px solid',
                borderColor: 'primary.main',
                '&:hover': { bgcolor: 'primary.dark', color: 'primary.contrastText' },
              }}
            >
              {loading ? <><CircularProgress size={20} sx={{ color: 'primary.main', mr: 1 }} />Signing in...</> : 'Sign In'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default LoginPage;
